<script setup lang="ts">
import type { Project } from '~/types/portfolio'
const props = defineProps<{ project: Project }>()
const root = ref<HTMLElement | null>(null)
const projectLinks = computed(() => ['site', 'github']
  .map(label => props.project.links?.find(item => item.label === label))
  .filter((item): item is NonNullable<Project['links']>[number] => Boolean(item)))
useReveal(root)
</script>

<template>
  <main ref="root" class="mx-auto max-w-4xl px-3 py-6 md:px-6 md:py-10">
    <NuxtLink to="/" class="text-sm text-accent-2">→ بازگشت به صفحه‌ی اصلی</NuxtLink>
    <div class="card-base mt-4 overflow-hidden">
      <div v-if="project.media?.url" class="relative aspect-[16/9] bg-surface-2">
        <img
          :src="project.media.url"
          :alt="project.media.alt || project.title"
          :width="project.media.width"
          :height="project.media.height"
          class="h-full w-full object-cover"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-bg-deep via-transparent"></div>
      </div>
      <div class="p-5 md:p-10">
        <h1 data-reveal class="text-2xl font-extrabold md:text-4xl">{{ project.title }}</h1>
        <p v-if="project.summary" data-reveal class="mt-2 text-sm text-muted">{{ project.summary }}</p>
        <!-- <p v-if="project.role || project.year" data-reveal class="mt-2 text-xs text-muted">
          {{ [project.role, project.year].filter(Boolean).join(' · ') }}
        </p> -->
        <div v-if="projectLinks.length" data-reveal class="mt-6 flex flex-wrap gap-3">
          <a
            v-for="item in projectLinks"
            :key="item.id || `${item.label}-${item.link}`"
            :href="item.link"
            target="_blank"
            rel="noopener noreferrer"
            class="group relative inline-flex min-h-11 items-center gap-2.5 overflow-hidden rounded-full border px-5 py-2.5 text-xs font-extrabold transition duration-300 before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:transition-transform before:duration-500 hover:-translate-y-0.5 hover:shadow-[var(--t-glow)] hover:before:translate-x-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            :class="item.label === 'site' ? 'border-accent bg-accent/15 text-accent-2' : 'border-line bg-surface-2 text-ink hover:border-accent hover:text-accent-2'"
          >
            <svg v-if="item.link.includes('github')" class="relative size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 .7a11.5 11.5 0 0 0-3.64 22.4c.58.1.79-.25.79-.56v-2.23c-3.22.7-3.9-1.37-3.9-1.37-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.71.08-.71 1.17.08 1.78 1.2 1.78 1.2 1.04 1.77 2.72 1.26 3.38.96.1-.75.4-1.26.74-1.55-2.57-.29-5.27-1.28-5.27-5.69 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.16 1.18a10.9 10.9 0 0 1 5.76 0c2.2-1.49 3.16-1.18 3.16-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.42-2.71 5.39-5.29 5.68.42.36.79 1.06.79 2.14v3.18c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .7Z" />
            </svg>
            <svg v-else class="relative size-4 fill-none stroke-current" viewBox="0 0 24 24" aria-hidden="true" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 5h5v5M10 14 19 5M19 13v6H5V5h6" />
            </svg>
            <span class="relative">{{ item.link.includes('github') ? 'مشاهده گیت‌هاب' : 'مشاهده سایت' }}</span>
          </a>
        </div>
        <div class="mt-6 grid gap-4">
          <div
            v-for="(paragraph, index) in project.description"
            :key="index"
            data-reveal
            class="text-sm leading-8 text-muted md:text-base"
            v-html="paragraph"
            >
          </div>
        </div>
        <ul v-if="project.tags?.length" class="mt-8 flex flex-wrap gap-2">
          <li v-for="t in project.tags" :key="t"
              class="rounded-full border border-line bg-accent/10 px-3 py-1.5 text-xs text-accent-2">{{ t }}</li>
        </ul>
      </div>
    </div>
  </main>
</template>
